import { useState, type FormEvent } from "react";
import { Plus } from "lucide-react";
import { actions, listOrganisationLinks, networkRequirementSummary } from "@/lib/api";
import type { Organisation } from "@/lib/types";
import { money, titleCase } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Select } from "@/components/ui/select";
import { useData } from "@/lib/app-view";
import { PageHeader } from "@/components/page-header";
import { Empty, Loading, ErrorState } from "@/components/empty-state";
import { Table } from "@/components/data-table";
import { FieldError } from "@/components/field-error";

export function NetworkStructure({
  organisation,
  refresh,
  organisations,
  platformOwner,
  changed,
}: {
  organisation: Organisation;
  refresh: number;
  organisations: Organisation[];
  platformOwner: boolean;
  changed: () => void;
}) {
  const [{ data: links, error, loading }, { data: summary }] = [
    useData(
      () => listOrganisationLinks(organisation.id),
      [organisation.id, refresh],
    ),
    useData(
      () => networkRequirementSummary(organisation.id).catch(() => []),
      [organisation.id, refresh],
    ),
  ];
  const [open, setOpen] = useState(false);
  if (loading) return <Loading />;
  if (error || !links) return <ErrorState message={error} />;
  return (
    <>
      <PageHeader
        eyebrow="GROUP OPERATIONS"
        title="Organisations & franchise network"
        description="Keep legal entities independent while sharing only the requirement or procurement context the relationship permits."
      >
        {platformOwner && (
          <Button onClick={() => setOpen(true)}>
            <Plus className="size-4" />
            Link organisation
          </Button>
        )}
      </PageHeader>
      {links.length ? (
        <Table
          heads={[
            "Parent organisation",
            "Linked organisation",
            "Relationship",
            "Operating model",
          ]}
        >
          {links.map((link) => (
            <tr key={link.id}>
              <td>
                <strong>{link.parent?.name}</strong>
              </td>
              <td>{link.child?.name}</td>
              <td>{titleCase(link.relationship)}</td>
              <td>{titleCase(link.sharing_level)}</td>
            </tr>
          ))}
        </Table>
      ) : (
        <Empty
          title="No linked organisations"
          description="Branches within one legal entity belong in Structure & budgets. Add a link here only when a franchise, subsidiary or managed organisation must remain a separate workspace."
        />
      )}
      {(summary || []).length > 0 && (
        <section className="mt-6">
          <h2 className="mb-3 font-serif text-2xl">
            Network requirement position
          </h2>
          <Table
            heads={[
              "Organisation",
              "Open requirements",
              "Pending approvals",
              "Estimated value",
            ]}
          >
            {(summary || []).map((row) => (
              <tr key={row.child_organisation_id}>
                <td>
                  <strong>{row.child_name}</strong>
                </td>
                <td>{row.open_requisitions}</td>
                <td>{row.pending_approvals}</td>
                <td>{money(row.estimated_value)}</td>
              </tr>
            ))}
          </Table>
        </section>
      )}
      <OrganisationLinkDialog
        open={open}
        onOpenChange={setOpen}
        parent={organisation}
        organisations={organisations}
        onDone={changed}
      />
    </>
  );
}

function OrganisationLinkDialog({
  open,
  onOpenChange,
  parent,
  organisations,
  onDone,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  parent: Organisation;
  organisations: Organisation[];
  onDone: () => void;
}) {
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    setBusy(true);
    setError("");
    try {
      await actions.linkOrganisations(
        parent.id,
        String(form.get("child")),
        String(form.get("relationship")),
        String(form.get("sharing")),
      );
      onOpenChange(false);
      onDone();
    } catch (reason) {
      setError((reason as Error).message);
    } finally {
      setBusy(false);
    }
  }
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader
          eyebrow="GROUP OPERATIONS"
          title="Link an independent organisation"
          description="The linked organisation retains its own people, data boundary, branding and policies."
        />
        <form onSubmit={submit} className="grid gap-4 p-6">
          <Label>
            Linked organisation
            <Select name="child" required>
              {organisations
                .filter((item) => item.id !== parent.id)
                .map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.name}
                  </option>
                ))}
            </Select>
          </Label>
          <Label>
            Relationship
            <Select name="relationship">
              <option value="franchise">Franchise</option>
              <option value="subsidiary">Subsidiary</option>
              <option value="managed_procurement">
                Managed procurement client
              </option>
            </Select>
          </Label>
          <Label>
            Sharing model
            <Select name="sharing">
              <option value="requirements_only">
                Requirements visible to parent
              </option>
              <option value="procurement_managed">
                Parent manages sourcing
              </option>
              <option value="summary_only">Consolidated summary only</option>
            </Select>
          </Label>
          <FieldError>{error}</FieldError>
          <div className="flex justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button disabled={busy || organisations.length < 2} type="submit">
              Link organisation
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
